/**
 * BottomNav — нижняя навигация в стиле Telegram с динамической ролевой моделью
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useMemo } from 'react';
import { useAppSettings, useUiStore } from '../../stores/appSettings';
import { useTelegram } from '../../hooks/useTelegram';
import { Icon } from '@tg-miniapp/ui-kit';
import {
  ClipboardList,
  CalendarDays,
  Gem,
  BarChart3,
  Users,
  Settings,
  FileText,
  User,
  ArrowUp,
  ArrowLeft,
  Bot,
  CreditCard,
  Ticket,
  Coins,
  type LucideIcon
} from 'lucide-react';

interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
}

const topAdminItems: NavItem[] = [
  { path: '/', label: 'Обзор', icon: BarChart3 },
  { path: '/owners', label: 'Владельцы', icon: Users },
  { path: '/ai-agents', label: 'ИИ Агенты', icon: Bot },
  { path: '/settings', label: 'Настройки', icon: Settings },
];

const bottomAdminItems: NavItem[] = [
  { path: '/logs', label: 'Логи', icon: FileText },
  { path: '/billing', label: 'Биллинг', icon: CreditCard },
  { path: '/promo', label: 'Промокоды', icon: Ticket },
  { path: '/finance', label: 'Финансы', icon: Coins },
];

export function BottomNav(): React.JSX.Element | null {
  const location = useLocation();
  const navigate = useNavigate();
  const { haptic } = useTelegram();
  const { currentRole, isClientMode } = useAppSettings();
  const { hideBottomNav } = useUiStore();

  const navItems = useMemo<NavItem[]>(() => {
    // 0. B2C Client Mode
    if (isClientMode) {
      return [
        { path: '/', label: 'Каталог', icon: ClipboardList },
        { path: '/client-history', label: 'Визиты', icon: CalendarDays },
        { path: '/client-loyalty', label: 'Лояльность', icon: Gem },
      ];
    }

    // 1. Super Admin (Платформа) — обрабатывается отдельно
    if (currentRole === 'admin') {
      return [];
    }

    // Считываем права доступа текущей роли сотрудника
    const activeMembership = useAppSettings.getState().memberships.find(
      m => m.businessId === useAppSettings.getState().currentBusinessId && m.role === currentRole
    );
    const activePerms = activeMembership?.permissions || {};

    // 2. Business Owner (Редуцировано до 4 пунктов) - Владелец имеет полный доступ
    if (currentRole === 'owner') {
      return [
        { path: '/', label: 'Аналитика', icon: BarChart3 },
        { path: '/bookings', label: 'Записи', icon: CalendarDays },
        { path: '/staff', label: 'Команда', icon: Users },
        { path: '/settings', label: 'Настройки', icon: Settings },
      ];
    }

    // 3. Все остальные роли (Manager, Master, Assistant, Custom роли) фильтруются на основе activePerms
    const items = [
      { path: '/', label: currentRole === 'manager' ? 'Обзор' : 'График', icon: currentRole === 'manager' ? BarChart3 : CalendarDays },
      { path: '/bookings', label: currentRole === 'manager' ? 'Все записи' : 'Записи', icon: currentRole === 'manager' ? CalendarDays : FileText },
      { path: '/staff', label: currentRole === 'manager' ? 'Мастера' : 'Команда', icon: Users },
      { path: '/settings', label: currentRole === 'manager' ? 'Настройки' : 'Профиль', icon: currentRole === 'manager' ? Settings : User },
    ];

    return items.filter(item => {
      if (item.path === '/') {
        return activePerms.viewAnalytics !== false;
      }
      if (item.path === '/bookings') {
        return activePerms.manageSchedule !== false || activePerms.viewAllBookings !== false;
      }
      if (item.path === '/staff') {
        return activePerms.manageStaff !== false;
      }
      if (item.path === '/settings') {
        return activePerms.editBotSettings !== false;
      }
      return true;
    });
  }, [currentRole, isClientMode]);

  const renderNavItem = (item: NavItem) => {
    const isActive = location.pathname === item.path;
    return (
      <button
        key={item.path}
        id={`nav-${item.path.replace('/', '') || 'home'}`}
        role="tab"
        aria-selected={isActive}
        aria-label={item.label}
        tabIndex={0}
        onClick={() => {
          haptic.impact('light');
          navigate(item.path);
        }}
        className={`flex flex-col items-center justify-center flex-1 min-w-0 py-1.5 px-0.5 rounded-lg transition-all duration-200 active:scale-95 outline-none focus-visible:ring-2 focus-visible:ring-[var(--tg-theme-button-color,#3b82f6)] ${
          isActive 
            ? 'bg-[var(--tg-theme-button-color,#3b82f6)]/10 text-[var(--tg-theme-button-color,#3b82f6)] font-extrabold' 
            : 'text-tg-theme-hint hover:bg-black/5 dark:hover:bg-white/5'
        }`}
      >
        <Icon 
          icon={item.icon} 
          size="sm" 
          variant={isActive ? 'active' : 'default'} 
          filled={isActive} 
          className={`mb-0.5 ${isActive ? 'text-[var(--tg-theme-button-color,#3b82f6)]' : ''}`}
        />
        <span className={`text-[8.5px] font-extrabold uppercase tracking-tight text-center block truncate w-full ${isActive ? 'opacity-100' : 'opacity-65'}`}>
          {item.label}
        </span>
      </button>
    );
  };

  const renderBackButton = () => {
    const isHome = location.pathname === '/' || location.pathname === '';
    return (
      <button
        id="nav-back"
        role="tab"
        aria-label="Назад"
        disabled={isHome}
        onClick={() => {
          haptic.impact('light');

          const event = new CustomEvent('app-back-button', { cancelable: true });
          const isPrevented = !window.dispatchEvent(event);
          if (isPrevented) return;

          const currentPath = location.pathname;
          if (currentPath === '/' || currentPath === '') {
            navigate(-1);
          } else {
            const segments = currentPath.split('/').filter(Boolean);
            if (segments.length <= 1) {
              navigate('/');
            } else {
              navigate('/' + segments.slice(0, -1).join('/'));
            }
          }
        }}
        className={`flex flex-col items-center justify-center flex-1 min-w-0 py-1.5 px-0.5 rounded-lg transition-all duration-200 active:scale-95 outline-none focus-visible:ring-2 focus-visible:ring-[var(--tg-theme-button-color,#3b82f6)] text-tg-theme-hint hover:bg-black/5 dark:hover:bg-white/5 ${
          isHome ? 'opacity-20 pointer-events-none' : 'opacity-100'
        }`}
      >
        <Icon icon={ArrowLeft} size="sm" />
        <span className="text-[8.5px] font-extrabold uppercase tracking-tight opacity-65 text-center block truncate w-full">Назад</span>
      </button>
    );
  };

  const renderUpButton = () => {
    const isHome = location.pathname === '/' || location.pathname === '';
    return (
      <button
        id="nav-up"
        role="tab"
        aria-label="Главная"
        onClick={() => {
          haptic.impact('light');
          if (isHome) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            navigate('/');
          }
        }}
        className="flex flex-col items-center justify-center flex-1 min-w-0 py-1.5 px-0.5 rounded-lg transition-all duration-200 active:scale-95 outline-none focus-visible:ring-2 focus-visible:ring-[var(--tg-theme-button-color,#3b82f6)] text-tg-theme-hint hover:bg-black/5 dark:hover:bg-white/5"
      >
        <Icon icon={ArrowUp} size="sm" />
        <span className="text-[8.5px] font-extrabold uppercase tracking-tight opacity-65 text-center block truncate w-full">Вверх</span>
      </button>
    );
  };


  // Двухстрочная панель для супер-админа
  if (currentRole === 'admin' && !isClientMode) {
    return (
      <nav 
        aria-label="Нижняя навигация супер-админа"
        className={`fixed bottom-0 left-0 right-0 z-50 bg-tg-theme-bg/95 backdrop-blur-3xl border-t border-black/10 dark:border-white/15 shadow-[0_-10px_30px_rgba(0,0,0,0.06)] transition-[transform,opacity] duration-300 ease-out transform ${
          hideBottomNav ? 'translate-y-36 opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'
        }`}
      >
        <div className="w-full flex flex-col gap-1 p-1 px-1.5 pb-[calc(6px+env(safe-area-inset-bottom))] overflow-hidden">
          {/* Верхняя строка */}
          <div role="tablist" className="w-full flex items-center justify-between">
            {topAdminItems.map((item) => renderNavItem(item))}
            {renderUpButton()}
          </div>
          
          {/* Нижняя строка */}
          <div role="tablist" className="w-full flex items-center justify-between border-t border-black/5 dark:border-white/15 pt-1">
            {bottomAdminItems.map((item) => renderNavItem(item))}
            {renderBackButton()}
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav 
      aria-label="Нижняя навигация"
      className={`fixed bottom-0 left-0 right-0 z-50 bg-tg-theme-bg/95 backdrop-blur-3xl border-t border-black/10 dark:border-white/15 shadow-[0_-10px_30px_rgba(0,0,0,0.06)] transition-[transform,opacity] duration-300 ease-out transform ${
        hideBottomNav ? 'translate-y-24 opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'
      }`}
    >
      <div 
        role="tablist"
        className="w-full flex items-center justify-between p-1 px-1.5 pb-[calc(6px+env(safe-area-inset-bottom))] overflow-hidden"
      >
        {navItems.map((item) => renderNavItem(item))}
        {renderBackButton()}
        {renderUpButton()}
      </div>
    </nav>
  );
}
